import * as yup from 'yup'
import { LockIcon } from '~/assets/icons'
import { useDispatch } from 'react-redux'
import { mutualTheme } from '~/utils/theme'
import { strip, isValid } from '@fnando/cnpj'
import { useSignIn } from '~/store/auth/useSignIn'
import React, { useCallback, useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { resetSignInState } from '~/store/auth/actions'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useHistory, useLocation } from 'react-router-dom'
import { trackEvent, AuthActions } from '~/service/analytics'
import { InputAdornment, Link, Typography, Box } from '@material-ui/core'
import { HfUsernameInput, HfPasswordInput, Badge, LoginSubmitButton } from '~/components'

type FormValues = {
  username: string,
  password: string
}

const validationSchema = yup.object().shape({
  password: yup.string().required('Campo obrigatório').matches(/^\d{6}$/, 'A senha deve conter 6 dígitos'),
  username: yup.string().required('Campo obrigatório').test('Valid CNPJ', 'CNPJ inválido', cnpj => cnpj ? isValid(cnpj) : true)
})

const initialFormValues: FormValues = {
  username: '',
  password: ''
}

export const LoginForm: React.FC = () => {
  const location = useLocation()
  const { handleSubmit, control, formState: { isValid, isSubmitted }, watch } = useForm<FormValues>({
    defaultValues: {
      ...initialFormValues,
      username: location.state ? String(location.state) : ''
    },
    resolver: yupResolver(validationSchema)
  })
  const history = useHistory()
  const [signInState, signIn] = useSignIn()
  const { loading, error } = signInState
  const dispatch = useDispatch()

  const username = watch('username')
  const password = watch('password')

  const hasError = isSubmitted && (error !== null || !isValid)

  useEffect(() => {
    dispatch(resetSignInState())
  }, [username, password])

  const onSubmit: SubmitHandler<FormValues> = useCallback(async () => {
    const success = await signIn({ username: strip(username), password })

    trackEvent(AuthActions.LOGIN)

    if (success) {
      trackEvent(AuthActions.SUCCESSFUL_PJ_LOGIN)
      history.push('/app')
      return
    }

    trackEvent(AuthActions.UNSUCCESSFUL_PJ_LOGIN)
  }, [signIn, history, username, password])

  const onRecoverPasswordPress = useCallback(() => {
    trackEvent(AuthActions.FORGOT_PASSWORD)
    history.push('/recover')
  }, [history])

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <HfUsernameInput
        acceptCNPJ
        name='username'
        control={control}
        removeIcon={false}
        disabled={loading}
        placeholder='Digite o CNPJ'
        error={hasError}
        sx={{
          marginTop: 4,
          marginBottom: 3
        }}
      />

      <HfPasswordInput
        label='Senha'
        name='password'
        control={control}
        disabled={loading}
        error={hasError}
        placeholder='Senha (6 dígitos, apenas números)'
        endAdornment={(
          <InputAdornment position='end'>
            <LockIcon htmlColor={mutualTheme.palette.textGray4.main} />
          </InputAdornment>
        )}
      />

      <Link
        onClick={onRecoverPasswordPress}
        sx={{
          cursor: 'pointer',
          alignSelf: 'flex-end',
          color: 'secondary.main',
          marginTop: 2
        }}
      >
        <Typography sx={{ fontWeight: '500' }} variant='airbnbBody2'>
          Esqueci minha senha
        </Typography>
      </Link>

      {hasError && (
        <Badge
          level='main'
          type='error'
          title='Os dados de login informados estão incorretos.'
          description='Verifique o CNPJ e senha informados e tente novamente.'
          icon='BlockIcon'
          hasError={hasError}
        />
      )}

      <LoginSubmitButton
        title='Acessar minha conta'
        loading={loading}
      />
    </Box >
  )
}
